import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ComponentProps } from 'react';
import type { CSSProperties } from 'react';
import { Avatar, Button, Icon, Popover, ProgressBar } from '@humanforce/mango-ui-react';
import type {
  LifecycleMotion,
  LifecycleRow,
  LifecycleStep,
  LifecycleStepState,
  LifecycleStepperProps,
} from './lifecycleStepperTypes';
import { useExclusivePopover } from './usePopoverGroup';
import './LifecycleStepper.css';

/**
 * An animated lifecycle stepper.
 *
 * Domain-free by design — it knows nothing about pay runs — so it can move to a shared package
 * unchanged. Progress values and destination states come from `steps`. Confirmed stage changes
 * receive an ordered visual handoff; action permissions remain live from the caller.
 *
 * Confirmed connector changes animate sequentially in either direction. `progress-line` is determinate and
 * preferred where the caller knows how far along the work is; the indeterminate variants only say
 * "something is moving". All of it is disabled under `prefers-reduced-motion` in the stylesheet.
 */

type IconName = ComponentProps<typeof Icon>['name'];

interface ConnectorTransition {
  id: string;
  from: number;
  to: number;
  direction: 'forward' | 'reverse';
}

const MIN_HANDOFF_MS = 2000;

const connectorPercent = (step: LifecycleStep): number => step.state === 'complete' ? 100
  : step.state === 'busy' || step.state === 'current' ? Math.min(100, Math.max(0, step.connectorPercent ?? 0)) : 0;

const STATE_ICON: Record<LifecycleStepState, IconName> = {
  complete: 'status-positive',
  busy: 'clock',
  current: 'clock',
  warning: 'status-caution',
  rejected: 'status-caution',
  upcoming: 'status-no-action',
  cancelled: 'status-no-action',
};

const ROW_ICON: Record<LifecycleRow['state'], IconName> = {
  complete: 'status-positive',
  current: 'clock',
  upcoming: 'status-no-action',
  rejected: 'status-caution',
};

/** Spoken suffix so state is never conveyed by colour alone. */
const STATE_DESCRIPTION: Record<LifecycleStepState, string> = {
  complete: 'completed',
  busy: 'in progress',
  current: 'current step',
  warning: 'needs attention',
  rejected: 'rejected',
  upcoming: 'not started',
  cancelled: 'cancelled',
};

const initialsOf = (name: string): string =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('');

const StepRows = ({ rows }: { rows: LifecycleRow[] }) => (
  <ul className="lifecycle-stepper__rows">
    {rows.map((row) => (
      <li key={row.id} className={`lifecycle-stepper__row lifecycle-stepper__row--${row.state}`}>
        <span className="lifecycle-stepper__row-icon" aria-hidden="true">
          <Icon name={ROW_ICON[row.state]} />
        </span>
        <span className="lifecycle-stepper__row-text">
          <span className="lifecycle-stepper__row-title mgo-text-s mgo-weight-strong">{row.title}</span>
          {row.detail && <span className="mgo-text-xs mgo-color-muted">{row.detail}</span>}
        </span>
        {row.person && (
          <Avatar
            size="small"
            label={row.person.name}
            initials={row.person.initials ?? initialsOf(row.person.name)}
            image={row.person.imageUrl}
          />
        )}
      </li>
    ))}
  </ul>
);

const StepMenu = ({ step, busy }: { step: LifecycleStep; busy: boolean }) => (
  // The popover renders through a portal, so the step’s own class is out of reach here and the
  // state has to travel with the menu for its colours to resolve.
  <div className={`lifecycle-stepper__menu lifecycle-stepper__menu--${step.state}`}>
    {step.menuLabel && (
      <span className="lifecycle-stepper__menu-label mgo-text-xs mgo-weight-strong mgo-color-muted">
        {step.menuLabel}
      </span>
    )}
    {step.rows && step.rows.length > 0 && <StepRows rows={step.rows} />}
    {step.footer && (
      <div className="lifecycle-stepper__footer mgo-stack mgo-gap-xs">
        {typeof step.footer.percent === 'number' && (
          <ProgressBar value={step.footer.percent} label={step.footer.title} tone="neutral" />
        )}
        <span className="lifecycle-stepper__footer-title mgo-text-xs mgo-weight-strong">{step.footer.title}</span>
        {step.footer.detail && <span className="mgo-text-xs mgo-color-muted">{step.footer.detail}</span>}
      </div>
    )}
    {step.action && (
      <div className="lifecycle-stepper__action">
        <Button
          label={step.action.label}
          size="small"
          fullWidth
          disabled={busy || step.action.disabled}
          onClick={step.action.onSelect}
        />
      </div>
    )}
  </div>
);

const StepperStep = ({
  step,
  isCurrent,
  isLast,
  busy,
  motion,
  stepTestIdPrefix,
  menuTestIdPrefix,
  unlocking,
  entering,
  visualPercent,
  presentationState,
  showProgressHead,
  transition,
}: {
  step: LifecycleStep;
  isCurrent: boolean;
  isLast: boolean;
  busy: boolean;
  motion: LifecycleMotion;
  stepTestIdPrefix: string;
  menuTestIdPrefix: string;
  unlocking: boolean;
  entering: boolean;
  visualPercent: number;
  presentationState: LifecycleStepState;
  showProgressHead: boolean;
  transition: ConnectorTransition | null;
}) => {
  const popover = useExclusivePopover(step.id);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const headRef = useRef<HTMLButtonElement | null>(null);
  const headHadFocus = useRef(false);
  const determinate = typeof step.connectorPercent === 'number';
  const effectiveMotion: LifecycleMotion = motion === 'progress-line' && !determinate
    ? 'flowing-dashes'
    : motion;
  const interactive = ((step.rows?.length ?? 0) > 0 || Boolean(step.footer) || Boolean(step.action))
    && step.state !== 'upcoming'
    && step.state !== 'cancelled';
  const accessibleName = `${step.title}: ${step.detail} — ${STATE_DESCRIPTION[step.state]}${presentationState !== step.state ? '; visual transition in progress' : ''}`;
  // Keep the completed connector at 100% so a live transition has somewhere visible to finish.
  const underway = step.state === 'busy' || step.state === 'current';
  const percent = visualPercent;
  const marker = (
    <span className="lifecycle-stepper__marker" aria-hidden="true">
      {presentationState === 'busy'
        ? <span className="lifecycle-stepper__spinner" />
        : <Icon name={presentationState === step.state && step.icon ? step.icon : STATE_ICON[presentationState]} />}
    </span>
  );

  return (
    <li
      className={`lifecycle-stepper__step lifecycle-stepper__step--${presentationState}${entering ? ' lifecycle-stepper__step--entering' : ''}`}
      aria-current={isCurrent ? 'step' : undefined}
    >
      <span className="lifecycle-stepper__node">
        {interactive
          ? (
            <Popover
              ref={popover.ref}
              onShow={popover.onShow}
              onHide={popover.onHide}
              testId={`${menuTestIdPrefix}-${step.id}`}
              trigger={(
                <button
                  ref={triggerRef}
                  type="button"
                  className="lifecycle-stepper__trigger"
                  aria-label={accessibleName}
                  aria-expanded={popover.isOpen}
                  data-testid={`${stepTestIdPrefix}-${step.id}`}
                >
                  {marker}
                </button>
              )}
            >
              <StepMenu step={step} busy={busy} />
            </Popover>
          )
          : (
            <span role="img" aria-label={accessibleName} data-testid={`${stepTestIdPrefix}-${step.id}`}>
              {marker}
            </span>
          )}
        <span className="lifecycle-stepper__text">
          <span className="mgo-text-s mgo-weight-strong">{step.title}</span>
          <span className="lifecycle-stepper__detail mgo-text-xs">{step.detail}</span>
        </span>
      </span>

      {!isLast && (
        <span
          className={`lifecycle-stepper__connector${unlocking ? ' lifecycle-stepper__connector--unlocking' : ''}${transition ? ' lifecycle-stepper__connector--transitioning' : ''} lifecycle-stepper__connector--${
            step.state === 'busy' ? effectiveMotion : 'static'
          }`}
          style={{
            '--lifecycle-progress': `${percent}%`,
            '--lifecycle-from': `${transition?.from ?? percent}%`,
            '--lifecycle-to': `${transition?.to ?? percent}%`,
          } as CSSProperties}
          data-transition-step={transition ? step.id : undefined}
          data-transition-direction={transition?.direction}
        >
          <span className="lifecycle-stepper__track" aria-hidden="true" />
          {(percent > 0 || transition) && <span key={`fill-${transition?.from}-${transition?.to}`} className="lifecycle-stepper__fill" aria-hidden="true" />}
          {showProgressHead && (transition || (underway && percent < 100 && motion === 'progress-line')) && (
            interactive
              ? <button
                  key={`head-${transition?.from}-${transition?.to}`}
                  ref={(node) => {
                    if (!node) {
                      headHadFocus.current = document.activeElement === headRef.current;
                      if (headHadFocus.current) triggerRef.current?.focus({ preventScroll: true });
                    }
                    headRef.current = node;
                    if (node && headHadFocus.current) node.focus({ preventScroll: true });
                  }}
                  type="button"
                  className="lifecycle-stepper__head lifecycle-stepper__head--interactive"
                  aria-label={`${step.title}: view progress details`}
                  aria-expanded={popover.isOpen}
                  onClick={() => triggerRef.current?.click()}
                />
              : <span key={`head-${transition?.from}-${transition?.to}`} className="lifecycle-stepper__head" aria-hidden="true" />
          )}
          {step.state === 'busy' && effectiveMotion === 'comet' && <span className="lifecycle-stepper__comet" />}
        </span>
      )}
    </li>
  );
};

export const LifecycleStepper = ({
  steps,
  currentStepId = null,
  busy = false,
  motion = 'progress-line',
  label = 'Lifecycle progress',
  testId,
  stepTestIdPrefix = 'lifecycle-step',
  menuTestIdPrefix = 'lifecycle-step-menu',
}: LifecycleStepperProps) => {
  const desired = useMemo(() => steps.slice(0, -1).map((step) => ({ id: step.id, state: step.state, percent: connectorPercent(step) })), [steps]);
  const [settled, setSettled] = useState<Record<string, number>>(() => Object.fromEntries(desired.map(({ id, percent }) => [id, percent])));
  const [transition, setTransition] = useState<ConnectorTransition | null>(null);
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return;
    const update = () => setReducedMotion(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  // Confirmed stage changes always get a visible handoff, even when the backend responds fast.
  // Partial percentages remain measured progress; server refreshes cannot cancel a running sweep.
  useLayoutEffect(() => {
    if (reducedMotion || motion === 'none') {
      if (transition) setTransition(null);
      if (desired.some(({ id, percent }) => settled[id] !== percent)) {
        setSettled(Object.fromEntries(desired.map(({ id, percent }) => [id, percent])));
      }
      return;
    }
    if (transition) return;
    const changed = desired.filter(({ id, percent }) => percent !== (settled[id] ?? percent));
    const handoffs = changed.filter(({ id, state, percent }) => percent === 100 || settled[id] === 100
      || state === 'upcoming' || state === 'cancelled');
    const backwards = handoffs.filter(({ id, percent }) => percent < settled[id]);
    const next = backwards.at(-1) ?? handoffs[0];
    if (next) setTransition({ id: next.id, from: settled[next.id], to: next.percent, direction: backwards.length ? 'reverse' : 'forward' });
    else if (changed.length > 0) setSettled((previous) => ({
      ...previous,
      ...Object.fromEntries(changed.map(({ id, percent }) => [id, percent])),
    }));
  }, [desired, settled, transition, reducedMotion, motion]);

  useEffect(() => {
    if (!transition) return;
    const timer = window.setTimeout(() => {
      setSettled((previous) => ({ ...previous, [transition.id]: transition.to }));
      setTransition(null);
    }, MIN_HANDOFF_MS);
    return () => window.clearTimeout(timer);
  }, [transition]);

  const transitionIndex = transition ? steps.findIndex((step) => step.id === transition.id) : -1;
  const unlockingIndex = transition?.direction === 'forward' ? transitionIndex : -1;
  return (
    <ol className="lifecycle-stepper" aria-label={label} data-testid={testId}
      style={{ '--lifecycle-handoff-duration': `${MIN_HANDOFF_MS}ms` } as CSSProperties}>
      {steps.map((step, index) => (
        <StepperStep
          key={step.id}
          step={step}
          isCurrent={currentStepId === step.id}
          isLast={index === steps.length - 1}
          busy={busy}
          motion={motion}
          stepTestIdPrefix={stepTestIdPrefix}
          menuTestIdPrefix={menuTestIdPrefix}
          unlocking={index === unlockingIndex}
          entering={unlockingIndex >= 0 && index === unlockingIndex + 1}
          visualPercent={settled[step.id] ?? connectorPercent(step)}
          presentationState={transition
            ? transition.direction === 'forward' && index > transitionIndex
              ? 'upcoming'
              : transition.direction === 'reverse' && index <= transitionIndex && settled[step.id] === 100
                ? 'complete'
                : step.state
            : step.state}
          showProgressHead={!transition || transition.id === step.id}
          transition={transition?.id === step.id ? transition : null}
        />
      ))}
    </ol>
  );
};

export type { LifecycleStep, LifecycleStepperProps, LifecycleMotion };
